/* Verbatim port of renderConflicts from service/review/index.html
 * (~3028-3070) — §6 Conflicts & gaps.
 *
 * PARAMETERIZED (rule a): reads the global STATE — takes `state:
 * AppPayloadState` as an explicit first parameter.
 *
 * GUARD (same treatment as views/exec.ts): `typeof WS !== 'undefined' &&
 * WS.available === true` becomes `wsAvailable()` (state/ui.ts), which is
 * now backed by the real F2d workspace module. The WS-branch bodies
 * (taskFor/chipFor/trackedIn, the live status chips, the data-wsact click
 * wiring) are kept byte-identical to the original template — real content
 * when wsAvailable() is true, same as the live app.
 *
 * F3: innerHTML → lit render(). chipFor/trackedIn (local helpers, not
 * lib/templates.ts twins — they're conflicts.ts-only, not shared string
 * helpers another unconverted view still needs) now return TemplateResult
 * | typeof nothing instead of a string. The trailing
 * `el.querySelectorAll('[data-wsact]')` click wiring moves into chipFor's
 * own `@click` — preserving the ORIGINAL's exact click-time behavior of
 * re-resolving the task from `WS.tasks` by id (not reusing the
 * render-time closure's `t`), since that's what the original's click
 * handler did (`WS.tasks.find(x => x.id === Number(b.dataset.wsact))`)
 * rather than trusting a possibly-stale reference.
 *
 * F4 item 3 (store decoupling): subscribes to wsChangeStore (state/
 * wsChange.ts) below so a WS data change (a task PATCH landing, WS.init()
 * finishing its probe, etc.) re-renders this section — replacing the old
 * hardcoded `renderConflicts(state)` call state/workspace.ts's
 * `repaintAll()`/`init()` used to make directly.
 */
import { html, nothing, render } from 'lit';
import { join } from 'lit/directives/join.js';
import { entityHeadline } from '../derive/entity.ts';
import { cap } from '../lib/format.ts';
import { tipT } from '../lib/templates.ts';
import { wsAvailable } from '../state/ui.ts';
import { payloadStore } from '../state/payload.ts';
import { wsChangeStore } from '../state/wsChange.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { WsTask } from '../types/workspace.ts';

export function renderConflicts(state: AppPayloadState): void {
  const el = document.getElementById('conflictsBody')!;
  const conflicts = state.conflicts || [];
  const opps = state.opportunities || [];
  const eh = entityHeadline(state);
  const finding = `${conflicts.length} factual conflict${conflicts.length!==1?'s':''} run across the footprint, ${opps.length} profiles could be claimed, created or fixed${eh && eh.weak ? `, and ${eh.weak} official profiles are still unverified as the client's` : ''}.`;

  // every finding here is one row of the master list (§7). When the shared
  // board is live, each row shows — and can change — its live task status.
  const wsOn = wsAvailable();
  const taskFor = (f: (x: WsTask) => boolean) => wsOn ? WS.tasks.find(f) : null;
  const chipFor = (t: WsTask | null | undefined) => t ? html`<button class="wsst s-${t.status}" data-wsact="${t.id}" style="margin-left:auto;flex-shrink:0" title="${WS.STATUS_FULL[t.status]||t.status} — click to change" @click=${(ev: MouseEvent) => {
    ev.stopPropagation();
    // re-resolve by id at click time (not the closed-over `t`) — same as
    // the original's WS.tasks.find(x => x.id === Number(b.dataset.wsact))
    const fresh = WS.tasks.find((x) => x.id === t.id);
    if (fresh) WS.statusMenu(ev.currentTarget as HTMLElement, fresh);
  }}>${WS.STATUS_LABEL[t.status] || t.status}</button>` : nothing;
  const trackedIn = (label: string) => html`<span style="margin-left:auto;flex-shrink:0;font-size:11px;color:var(--muted)">tracked in <a href="#s7" style="color:var(--azure)">§7 → ${label}</a></span>`;

  render(html`
    <div class="finding">${finding}</div>
    <div class="why"><b>Why this matters:</b> when the client's own surfaces disagree — two official websites, two CEO spellings, three employee counts — every downstream system (Google, AI engines, directories) picks one at random. Fixing conflicts at the source is the cheapest reputation work there is. Each item below carries its receipts${wsOn ? ' — and its live status from the shared board' : ''}.</div>

    ${conflicts.length ? html`<h3 style="font-size:16px;margin-bottom:10px">Factual conflicts <span style="font-weight:500;color:var(--muted);font-size:12px">from the consistency audit — with the URLs that disagree</span></h3>` : nothing}
    ${conflicts.map((c) => {
      const t = taskFor((x) => x.action_key.startsWith('conflict:') && x.detail === c.description);
      return html`<div class="conflict"><div style="display:flex;gap:10px;align-items:flex-start"><p style="flex:1">${c.description}</p>${chipFor(t)}</div>
      <div class="urls">${join((c.involvedUrls||[]).map((u) => html`<a href="${u}" target="_blank" rel="noopener">${u}</a>`), html`<br>`)}</div></div>`;
    })}

    ${opps.length ? html`<h3 style="font-size:16px;margin:22px 0 10px">Missing & unclaimed <span style="font-weight:500;color:var(--muted);font-size:12px">profiles the client could claim, create or fix</span></h3>` : nothing}
    ${opps.map((o) => {
      const t = taskFor((x) => x.action_key.startsWith('opp:') && x.detail === o.rationale);
      const isWd = wsOn && !t && /wikidata/i.test(o.platform || '');
      return html`<div class="conflict gap"><div style="display:flex;gap:10px;align-items:flex-start"><p style="flex:1"><b>${o.action === 'claim' ? 'Claim' : o.action === 'create' ? 'Create' : 'Fix'} — ${o.platform}</b> <span class="prio ${o.priority||'low'}" style="margin-left:6px">${cap(o.priority||'')}</span></p>${t ? chipFor(t) : isWd ? trackedIn('Update Wikidata (worksheet)') : nothing}</div>
      <p style="font-size:12.5px;color:var(--ink-2)">${o.rationale}</p></div>`;
    })}

    ${eh && eh.weak ? (() => {
      const t = taskFor((x) => x.action_key === 'cross-links');
      return html`<h3 style="font-size:16px;margin:22px 0 10px">Unverified links</h3>
    <div class="conflict weak"><div style="display:flex;gap:10px;align-items:flex-start"><p style="flex:1">${eh.weak} official profiles have no authoritative cross-reference (site ${tipT('sameAs','sameAs')} or Wikidata). The full list and the one-snippet fix live in <a href="#s2" style="color:var(--azure);font-weight:600">§2 →</a></p>${chipFor(t)}</div></div>`;
    })() : nothing}
    ${wsOn ? html`<div class="caption">every item above is a row on the shared action board (§7) — status changes here update everywhere, including the exec top-five</div>` : nothing}`, el);
}

// F4 item 3 — see the module doc comment: re-renders §6 whenever WS's data
// changes, replacing the direct renderConflicts(state) call
// state/workspace.ts's repaintAll()/init() used to make.
wsChangeStore.subscribe(() => renderConflicts(payloadStore.get()));
